import {forwardRef, useCallback} from "react";
import {IProtocolStepDefinition} from "../../types/protocolDefinitions";
import {Box, BoxProps, Text, TextInput} from "@mantine/core";
import {IconGripVertical} from "@tabler/icons-react";
import {StepIcon} from "../../common/StepIcon";
import {StepView, TStepPartialUpdate} from "../../common/steps/StepView";
import cx from "clsx";
import stepViewRowClasses from "../../common/steps/StepView.module.css";
import planningStepClasses from "./PlanningStepView.module.css";
import commonClasses from "../../../common/common.module.css";
import {Instructions, StepKindDisplayField} from "@/protocols/common/steps/fields";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {IProtocolStepKind} from "@/protocols/types/stepKind.ts";

interface PlanningStepViewRowProps extends BoxProps {
    step: IProtocolStepDefinition
    index: number
    selected?: boolean
    onSelect?: () => void
    onChange: (updates: DeepPartial<IProtocolStepDefinition>) => void
    dragHandleProps?: any
}


export const PlanningStepView = forwardRef<HTMLDivElement, PlanningStepViewRowProps>(({
                                                                                          step,
                                                                                          index,
                                                                                          selected = false,
                                                                                          onSelect,
                                                                                          onChange,
                                                                                          dragHandleProps,
                                                                                          ...others
                                                                                      }, ref) => {
    // Pass partial updates from StepView directly to onChange
    const handleStepViewFieldsChange = useCallback((updates: TStepPartialUpdate) => {
        onChange(updates as DeepPartial<IProtocolStepDefinition>);
    }, [onChange]);

    return (
        <Box
            ref={ref}
            className={cx(stepViewRowClasses.row, commonClasses.hover, {[stepViewRowClasses.selected]: selected})}
            onClick={onSelect}
            {...others}
        >
            {/* Row 1: Drag handle (only in groups), step number, icon, name */}
            <Box className={planningStepClasses.row1}>
                {dragHandleProps ? (
                    <Box className={stepViewRowClasses.dragHandleContainer} {...dragHandleProps}>
                        <IconGripVertical size="1rem" className={stepViewRowClasses.dragHandle}/>
                        <Text className={stepViewRowClasses.stepNumber}>{index + 1}</Text>
                    </Box>
                ) : <Box/>}
                <Box className={stepViewRowClasses.stepIcon}>
                    <StepIcon kind={step.kind}/>
                </Box>
                <StepKindDisplayField kind={step.kind} size={"xs"} mr={"0.5rem"}/>
                <TextInput
                    size={"xxs"}
                    value={step.name}
                    onChange={(ev) => onChange({name: ev.currentTarget.value})}
                />
            </Box>
            {step.kind !== IProtocolStepKind.INSTRUCTIONS ?
                <Instructions instructions={step.instructions || ""} editingMode="full"
                              onChange={(newInstructions) => onChange({instructions: newInstructions})}/> : <></>}
            {/* Row 2: StepView fields - all fields are editable inline */}
            <Box mt={"0.5rem"} className={stepViewRowClasses.stepViewRow}>
                <StepView
                    key={step.id}
                    step={step}
                    workflow={"planning"}
                    onChange={handleStepViewFieldsChange}
                />
            </Box>
        </Box>
    );
});
